import type { RowBase } from "../data-table.js";
import type { FilterData } from "../data-table.types.js";

export const matchesFilter = <TRow extends RowBase>(
  row: TRow,
  columnKey: keyof TRow,
  filterData: FilterData
): boolean => {
  const { values: selectedValues, nestedKey } = filterData;
  const rowValue = row[columnKey];

  if (Array.isArray(rowValue)) {
    return matchesArrayFilter(rowValue, selectedValues, nestedKey);
  }
  if (nestedKey && typeof rowValue === "object" && rowValue !== null) {
    const nestedValue = getNestedValue(rowValue as RowBase, nestedKey);
    return matchesSingleValueFilter(nestedValue, selectedValues);
  }

  return matchesSingleValueFilter(rowValue, selectedValues);
};

const matchesArrayFilter = (
  rowValue: RowBase[],
  selectedValues: (string | number)[],
  nestedKey?: string
): boolean => {
  const extractedValues = rowValue
    .map((item: RowBase) => {
      if (typeof item === "object" && item !== null) {
        if (nestedKey && nestedKey in item) {
          return item[nestedKey];
        }
        const values = Object.values(item);
        return values.find((v) => typeof v === "number" || typeof v === "string");
      }
      return item;
    })
    .filter((v) => v !== undefined);

  return selectedValues.some((filterVal) =>
    extractedValues.some((extractedVal) => compareValues(filterVal, extractedVal))
  );
};

const matchesSingleValueFilter = (
  rowValue: unknown,
  selectedValues: (string | number)[]
): boolean => {
  return selectedValues.some((filterVal) => compareValues(filterVal, rowValue));
};

const compareValues = (filterVal: string | number, rowVal: unknown): boolean => {
  const filterNum = Number(filterVal);
  const rowNum = Number(rowVal);

  if (!isNaN(filterNum) && !isNaN(rowNum)) {
    return filterNum === rowNum;
  }

  return String(filterVal) === String(rowVal);
};

const getNestedValue = (obj: RowBase, path: string): unknown => {
  const keys = path.split(".");
  let value: unknown = obj;

  for (const key of keys) {
    if (value && typeof value === "object" && key in value) {
      value = (value as RowBase)[key];
    } else {
      return undefined;
    }
  }

  return value;
};

const rowIdCache = new WeakMap<object, string>();
let rowIdCounter = 0;
export const defaultGetRowId = <TRow extends RowBase>(row: TRow): string | number => {
  if (row.id != null) return row.id;
  let id = rowIdCache.get(row);
  if (!id) {
    id = `row-${rowIdCounter++}`;
    rowIdCache.set(row, id);
  }
  return id;
};
